import * as PIXI from 'pixi.js';
import { Tween } from './Tween';
import { TweenStack } from './TweenStack';
import { removeItem } from '../utils/array-utils';
import { Easing } from './Easing';

export class TweenManager {
  public currentTime: number = 0;
  public easing = Easing;

  private tweens: Tween[] = [];
  private tweensToAdd: Tween[] = [];
  private tweenStacks: TweenStack[] = [];

  constructor() {
    // game onPause - pauseOnGamePause
    // game onResume - resumeOnGameResume
  }

  public add(tween: Tween): void {
    // tween._manager = this;
    this.tweensToAdd.push(tween);
  }

  public create(target: object): Tween {
    const tween: Tween = new Tween(target, this);

    if (target instanceof PIXI.DisplayObject) {
      // @ts-ignore
      target.onDestroyed.addOnce(() => {
        if (this.isTweenActive(tween)) {
          tween.stop();
        }
      });
    }

    return tween;
  }

  public createTweenStack(target: object, multiplication: boolean = false): TweenStack {
    const stack: TweenStack = new TweenStack(target, multiplication, this);

    if (target instanceof PIXI.DisplayObject) {
      // @ts-ignore
      target.onDestroyed.addOnce(() => stack.destroy());
    }

    this.tweenStacks.push(stack);
    return stack;
  }

  public removeTweenStack(stack: TweenStack): void {
    removeItem(this.tweenStacks, stack);
  }

  public isTweenActive(tween: Tween): boolean {
    return !tween.pendingDelete && (this.tweens.includes(tween) || this.tweensToAdd.includes(tween));
  }

  public remove(tween: Tween): void {
    const i: number = this.tweens.indexOf(tween);

    if (i !== -1) {
      this.tweens[i].pendingDelete = true;
    } else {
      const iToAdd: number = this.tweensToAdd.indexOf(tween);
      if (iToAdd !== -1) {
        this.tweensToAdd[iToAdd].pendingDelete = true;
      }
    }
  }

  public update(deltaTime: number): boolean {
    this.currentTime += deltaTime;

    const stacksLen: number = this.tweenStacks.length;
    const addTweensLen: number = this.tweensToAdd.length;
    let tweensLen: number = this.tweens.length;

    if (tweensLen === 0 && addTweensLen === 0 && stacksLen === 0) {
      return false;
    }

    let i: number = 0;

    while (i < tweensLen) {
      if (this.tweens[i].update(this.currentTime, deltaTime)) {
        i++;
      } else {
        this.tweens.splice(i, 1);
        tweensLen--;
      }
    }

    if (addTweensLen > 0) {
      this.tweens = this.tweens.concat(this.tweensToAdd);
      this.tweensToAdd.length = 0;
    }

    for (const stack of this.tweenStacks) {
      stack.update();
    }

    return true;
  }

  public getAll(): Tween[] {
    return this.tweens;
  }

  public removeAll(): void {
    for (const tweenStack of this.tweenStacks) {
      tweenStack.destroy();
    }

    for (const tween of this.tweens) {
      tween.pendingDelete = true;
    }

    this.tweensToAdd = [];
  }

  public isTweening(target: object): boolean {
    return this.tweens.some((tween: Tween) => tween.target === target);
  }

  public pauseAll(): void {
    for (const tween of this.tweens) {
      tween.pause();
    }
  }

  public resumeAll(): void {
    for (const tween of this.tweens) {
      tween.resume();
    }
  }

  public removeFrom(target: object | object[] | PIXI.Container | PIXI.Sprite, children: boolean = false): void {
    if (Array.isArray(target)) {
      for (const object of target) {
        this.removeFrom(object);
      }
    } else if (target instanceof PIXI.Container && children) {
      for (const child of target.children) {
        this.removeFrom(child);
      }
    } else {
      if (target instanceof PIXI.Sprite || target instanceof PIXI.Container) {
        this.removeFrom(target.scale);
        this.removeFrom(target.pivot);
      }

      this.tweens.filter((tween: Tween) => tween.target === target).forEach((tween: Tween) => this.remove(tween));

      this.tweensToAdd.filter((tween: Tween) => tween.target === target).forEach((tween: Tween) => this.remove(tween));
    }
  }

  private pauseOnGamePause(): void {
    for (const tween of this.tweens) {
      tween.pauseOnGamePause();
    }
  }

  private resumeOnGameResume(): void {
    for (const tween of this.tweens) {
      tween.resumeOnGameResume();
    }
  }
}
